import { useEffect, useRef, useState } from "react";
import { api, type JobFile, type JobFilesList } from "./api";
import { canApprove, useMe } from "./session";
import { href } from "./router";
import "./job.css";
import { DropFiles } from "./DropFiles";

const icon = (t: string, n: string) => t === "application/pdf" ? "📄" : t.startsWith("image/") ? "🖼️" : /sheet|excel|csv/.test(t) || /\.xlsx?$/i.test(n) ? "📊" : "📎";
const size = (b: number) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

/** Job Files: fixed folders, tap to open. Office uploads/archives; foremen view. */
export function JobFiles({ projectId, tick }: { projectId: string; tick: number }) {
  const office = canApprove(useMe());
  const [data, setData] = useState<JobFilesList | null>(null);
  const [folder, setFolder] = useState<string>("Schedule");
  const [archived, setArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const load = () => api.jobFiles(projectId, archived).then(setData).catch((e) => setError(e.message));
  useEffect(() => { void load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, tick, archived]);
  const upload = async (files: File[]) => {
    if (!files.length) return;
    setBusy(true); setError(null);
    try { const r = await api.uploadJobFiles(projectId, folder, files); if (r.failed.length) setError(r.failed.map((f) => `${f.fileName}: ${f.message}`).join(" ")); await load(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const act = async (f: JobFile, body: { archived?: boolean; folder?: string }) => { setError(null); try { await api.updateJobFile(projectId, f.id, body); await load(); } catch (e) { setError((e as Error).message); } };
  if (!data) return <div className="muted">{error ?? "Loading files…"}</div>;
  const count = (f: string) => data.files.filter((x) => x.folder === f).length + (archived ? 0 : data.pinFiles.filter((x) => x.folder === f).length);
  const shown = data.files.filter((x) => x.folder === folder);
  const pinShown = archived ? [] : data.pinFiles.filter((x) => x.folder === folder);
  return (
    <div className="jobfiles">
      <div className="chips folder-chips" role="tablist">
        {data.folders.map((f) => <button key={f} role="tab" aria-selected={folder === f} className={folder === f ? "chip on" : "chip"} onClick={() => setFolder(f)}>{f} ({count(f)})</button>)}
      </div>
      {office && !archived && <DropFiles testId="drop-jobfiles" disabled={busy} label={`Drop files to add to ${folder}`} onFiles={(fs) => void upload(fs)}><div className="jf-upload">
        <input ref={input} type="file" multiple hidden onChange={(e) => { void upload([...(e.target.files ?? [])]); e.target.value = ""; }} />
        <button className="btn primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? "Uploading…" : `+ Add to ${folder}`}</button>
        <span className="muted small">PDF, Excel, photos or any file. From a phone you can take a picture.<span className="drop-hint"> Or drag files here.</span></span>
      </div></DropFiles>}
      {folder === "Submittals" && !archived && <div className="muted small">Split packets and place submittals on plans in the <a href={href({ name: "project", id: projectId, tab: "submittals" })}>Submittals tab</a>.</div>}
      {error && <div className="error">{error}</div>}
      {!shown.length && !pinShown.length && <div className="empty">{archived ? "Nothing archived here." : `No ${folder.toLowerCase()} yet.${office ? "" : " The office adds these."}`}</div>}
      <div className="jf-list">
        {shown.map((f) => (
          <div key={f.id} className="card jf-row">
            <a className="jf-open" href={api.jobFileUrl(projectId, f.id)} target="_blank" rel="noreferrer">
              <span className="jf-icon">{icon(f.contentType, f.name)}</span>
              <span className="grow"><span className="title">{f.name}</span><span className="muted small">{size(f.size)} · {new Date(f.addedAt).toLocaleDateString()} · {f.addedBy}{f.archivedAt ? ` · archived ${new Date(f.archivedAt).toLocaleDateString()}` : ""}</span></span>
            </a>
            <div className="jf-actions">
              <a className="btn small" href={api.jobFileUrl(projectId, f.id, true)} aria-label={`Download ${f.name}`}>⬇</a>
              {office && (archived
                ? <button className="btn small" onClick={() => void act(f, { archived: false })}>Restore</button>
                : <>
                  <select className="small" aria-label="Move to folder" value={f.folder} onChange={(e) => void act(f, { folder: e.target.value })}>{data.folders.map((x) => <option key={x}>{x}</option>)}</select>
                  <button className="btn small" onClick={() => { if (confirm(`Archive "${f.name}"? It's hidden but kept, and can be restored.`)) void act(f, { archived: true }); }}>Archive</button>
                </>)}
            </div>
          </div>
        ))}
        {pinShown.map((p) => (
          <div key={p.attachmentId} className="card jf-row">
            <a className="jf-open" href={api.pinFileUrl(p.pinId, p.attachmentId)} target="_blank" rel="noreferrer">
              <span className="jf-icon">📍</span>
              <span className="grow"><span className="title">{p.name}</span><span className="muted small">On plan pin "{p.pinTitle}" · {p.pinStatus}</span></span>
            </a>
            <div className="jf-actions"><a className="btn small" href={href({ name: "viewer", id: p.documentId, page: 1, pin: p.pinId })}>Show pin</a></div>
          </div>
        ))}
      </div>
      {(data.archivedCount > 0 || archived) && office && <button className="btn small" onClick={() => setArchived(!archived)}>{archived ? "← Back to files" : `Archived (${data.archivedCount})`}</button>}
    </div>
  );
}
